import { footprintCorner } from "../compile/footprint.ts";

/**
 * Placing props so their art sits where their collision is laid (ADR-040 amendments of 2026-10-03: the
 * author's ruling on the straddle, and "the stamp lays a footprint on the cells", P1.6-T78). The stamp lays a
 * footprint with the frame's bottom-left, or its bottom-right when mirrored, on the nearest cell corner
 * (`footprintCorner`), so a prop placed between cells blocks the footprint panel's cells up to half a cell
 * from its art. Moving the prop, art and footprint together, to that corner puts the art exactly over what
 * it blocks. The cell size is the tile size over the collision resolution, so any game's grid is honoured.
 */

/**
 * The nearest position to a prop's that puts its footprint's corner on a collision cell corner: the frame's
 * left edge (its right edge when mirrored, where the mirrored cells start) and its bottom edge each rounded to
 * the nearest cell edge, a move of at most half a cell on each axis, a tie going right and down. The stamp
 * lays the prop's footprint from this same corner wherever the prop stands.
 *
 * @param position The prop's anchor in tiles, on the pixel lattice.
 * @param frameWidth The frame's width in pixels.
 * @param flipX Whether the prop is mirrored.
 * @param tileSize Tile size in pixels.
 * @param cellsPerTile Collision cells per tile.
 * @returns The aligned anchor in tiles, on the pixel lattice.
 */
export function alignedToCells(position: { readonly x: number; readonly y: number }, frameWidth: number, flipX: boolean, tileSize: number, cellsPerTile: number): { x: number; y: number } {
  const corner = footprintCorner(Math.round(position.x * tileSize), Math.round(position.y * tileSize) + tileSize, frameWidth, flipX, tileSize / cellsPerTile);
  return { x: corner.left / tileSize, y: (corner.bottom - tileSize) / tileSize };
}

/**
 * How far a prop stands from the collision cells: the move in whole pixels that puts its frame's corner (the
 * bottom-left, the bottom-right when mirrored) on the cell corner the stamp lays its footprint from
 * (`footprintCorner`), at most half a cell on each axis, a tie going right and down. Zero on both axes for a
 * prop on the cells. The align command, the off-cell check and a dry run all read this one measure, so they
 * agree on which props are off the cells and by how much.
 *
 * @param position The prop's anchor in tiles, on the pixel lattice.
 * @param frameWidth The frame's width in pixels; read only for a mirrored prop.
 * @param flipX Whether the prop is mirrored.
 * @param tileSize Tile size in pixels.
 * @param cellsPerTile Collision cells per tile.
 * @returns Pixels right and pixels down.
 * @example cellOffset({ x: 100.875, y: 127.625 }, 96, false, 32, 4) is { dx: 4, dy: 4 }.
 */
export function cellOffset(position: { readonly x: number; readonly y: number }, frameWidth: number, flipX: boolean, tileSize: number, cellsPerTile: number): { dx: number; dy: number } {
  const left = Math.round(position.x * tileSize);
  const bottom = Math.round(position.y * tileSize) + tileSize;
  const corner = footprintCorner(left, bottom, frameWidth, flipX, tileSize / cellsPerTile);
  return { dx: corner.left - left, dy: corner.bottom - bottom };
}

/**
 * Where a prop goes when stepped by collision cells: on each axis stepped, its frame's corner moves to the
 * next cell edge that way, a whole cell from a prop on the cells and less from one between them, so a stepped
 * prop is on the cells on that axis. An axis not stepped is left as it is.
 *
 * @param position The prop's anchor in tiles, on the pixel lattice.
 * @param frameWidth The frame's width in pixels; read only for a mirrored prop.
 * @param flipX Whether the prop is mirrored.
 * @param tileSize Tile size in pixels.
 * @param cellsPerTile Collision cells per tile.
 * @param stepX Cells right: negative steps left, 0 leaves the axis alone.
 * @param stepY Cells down: negative steps up, 0 leaves the axis alone.
 * @returns The stepped anchor in tiles, on the pixel lattice.
 */
export function cellStepped(position: { readonly x: number; readonly y: number }, frameWidth: number, flipX: boolean, tileSize: number, cellsPerTile: number, stepX: number, stepY: number): { x: number; y: number } {
  const cell = tileSize / cellsPerTile;
  const reach = flipX ? frameWidth : 0;
  const step = (edge: number, cells: number): number => (cells === 0 ? edge : cells > 0 ? (Math.floor(edge / cell) + cells) * cell : (Math.ceil(edge / cell) + cells) * cell);
  const left = step(Math.round(position.x * tileSize) + reach, stepX) - reach;
  const top = step(Math.round(position.y * tileSize), stepY);
  return { x: left / tileSize, y: top / tileSize };
}
